// trade-concierge.js — Trade Concierge brief island (P1, Option A). Match runs in-page.
// Loads ONLY on the gated trade page; returns early everywhere else. Feed fetch is
// async + non-blocking (warms while the designer fills the form).
//
// Results render as the REAL site product card — the SAME .card / .card-core /
// .card-img / .card-body / .card-collection / .card-name / .card-descriptor /
// .card-foot / .card-price / .card-fav DOM the collection grid uses (snippets/
// product-card.liquid) — inside the SAME .cards grid container, so global
// theme.css card styling applies verbatim and the trade edit reads as the rest
// of the site, not a bolted-on tool. The save heart reuses the .card-fav visual
// but wires to the trade BOARD (localStorage per project room), NOT global favs.
(function () {
  const root = document.querySelector('[data-tcb]');
  if (!root) return;
  const form = root.querySelector('[data-tcb-form]');
  if (!form) return; // gated/logged-out view: nothing to wire

  const M = window.ArteverkMatch; // matchRugs, tradePriceCents, formBriefToDesignBrief
  const slotsWrap = root.querySelector('[data-tcb-slots]');
  const tpl = root.querySelector('[data-tcb-slot-template]');
  const results = root.querySelector('[data-tcb-results]');
  let feedPromise = null;

  // Pointer-aware, non-blocking feed load. Resolves to an array of rug rows.
  function loadFeed() {
    if (!feedPromise) {
      feedPromise = fetch(root.dataset.feed)
        .then((r) => r.json())
        .then((d) => {
          if (Array.isArray(d.rugs)) return d.rugs;
          if (d.url) return fetch(d.url).then((r) => r.json()).then((f) => f.rugs || []);
          return [];
        });
    }
    return feedPromise;
  }
  function addSlot() { const s = tpl.content.firstElementChild.cloneNode(true); slotsWrap.appendChild(s); renumber(); return s; }
  function renumber() { slotsWrap.querySelectorAll('.tcb-slot').forEach((s, i) => { s.querySelector('[data-slot-n]').textContent = i + 1; }); }

  // ── Copy to next rug — clone the source slot's CURRENT selections into a fresh slot.
  // A designer briefing similar rooms shouldn't re-enter everything. We copy by VALUE
  // (not cloneNode) so the new slot is a clean template with the same answers applied:
  // style chips, colour swatches, the ft/in ranges, shape, budget, and every Refine-
  // further control (material chips, construction/tone/condition selects, OOAK switch).
  function duplicateSlot(src) {
    const dst = addSlot();
    // aria-pressed chips + swatches: mirror by data-attr so order/identity is exact.
    [['.tcb-chip[data-style]', 'style'], ['.tcb-sw[data-color]', 'color'], ['.tcb-chip[data-material]', 'material']]
      .forEach(([sel, key]) => {
        const on = new Set([...src.querySelectorAll(`${sel}[aria-pressed="true"]`)].map((b) => b.dataset[key]));
        dst.querySelectorAll(sel).forEach((b) => b.setAttribute('aria-pressed', String(on.has(b.dataset[key]))));
      });
    // Size quick-pick chip (single-select) — carry the pressed one over.
    const srcSize = src.querySelector('.tcb-chip[data-size][aria-pressed="true"]');
    if (srcSize) { const m = dst.querySelector(`.tcb-chip[data-size="${srcSize.dataset.size}"]`); if (m) m.setAttribute('aria-pressed', 'true'); }
    // ft/in ranges + shape/budget + construction/tone/condition selects (value copy).
    ['widthMinFt', 'widthMinIn', 'widthMaxFt', 'widthMaxIn', 'lengthMinFt', 'lengthMinIn', 'lengthMaxFt', 'lengthMaxIn',
     'shape', 'budgetMax', 'construction', 'tone', 'condition'].forEach((f) => {
      const a = src.querySelector(`[data-f="${f}"]`), b = dst.querySelector(`[data-f="${f}"]`);
      if (a && b) b.value = a.value;
    });
    // One-of-a-kind switch.
    const so = src.querySelector('[data-f="ooakOnly"]'), bo = dst.querySelector('[data-f="ooakOnly"]');
    if (so && bo) bo.checked = so.checked;
    // Open the Refine-further drawer on the copy if the source had any of its fields set,
    // so the carried-over details aren't hidden from view.
    const srcMore = src.querySelector('.tcb-more'), dstMore = dst.querySelector('.tcb-more');
    if (srcMore && dstMore && srcMore.open) dstMore.open = true;
    track('trade_rug_duplicated', {});
    dst.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return dst;
  }

  root.querySelector('[data-tcb-add]').addEventListener('click', addSlot);

  // Standard sizes → range-fill recipe. Rectangles: width/length each fill a
  // 1-foot band (W'0"–W'11", L'0"–L'11") so any rug in that foot fits. Runner:
  // shape=Runner, width 2'–3'11", length left open. Round: shape=Round, ranges open.
  const setRange = (s, f, v) => { const el = s.querySelector(`[data-f="${f}"]`); if (el) el.value = (v == null ? '' : v); };
  const clearRanges = (s) => ['widthMinFt', 'widthMinIn', 'widthMaxFt', 'widthMaxIn', 'lengthMinFt', 'lengthMinIn', 'lengthMaxFt', 'lengthMaxIn'].forEach((f) => setRange(s, f, ''));
  function applySize(s, size) {
    clearRanges(s);
    const shapeEl = s.querySelector('[data-f="shape"]');
    if (size === 'runner') {
      if (shapeEl) shapeEl.value = 'Runner';
      setRange(s, 'widthMinFt', 2); setRange(s, 'widthMinIn', 0);
      setRange(s, 'widthMaxFt', 3); setRange(s, 'widthMaxIn', 11);
      return; // length left open
    }
    if (size === 'round') { if (shapeEl) shapeEl.value = 'Round'; return; } // ranges open
    if (size === 'oversize') {
      // Palace / oversize: open-ended large. Set a floor (12'0" × 15'0") and leave
      // BOTH maxes empty (clearRanges already did) so there's no upper bound.
      setRange(s, 'widthMinFt', 12); setRange(s, 'widthMinIn', 0);
      setRange(s, 'lengthMinFt', 15); setRange(s, 'lengthMinIn', 0);
      return;
    }
    const m = /^(\d+)x(\d+)$/.exec(size);
    if (!m) return;
    const w = Number(m[1]), l = Number(m[2]);
    if (shapeEl) shapeEl.value = 'Rectangle';
    setRange(s, 'widthMinFt', w); setRange(s, 'widthMinIn', 0);
    setRange(s, 'widthMaxFt', w); setRange(s, 'widthMaxIn', 11);
    setRange(s, 'lengthMinFt', l); setRange(s, 'lengthMinIn', 0);
    setRange(s, 'lengthMaxFt', l); setRange(s, 'lengthMaxIn', 11);
  }

  slotsWrap.addEventListener('click', (e) => {
    // Duplicate-rug + remove-rug first so neither reads as a chip toggle.
    if (e.target.closest('[data-tcb-duplicate]')) {
      duplicateSlot(e.target.closest('.tcb-slot'));
      return;
    }
    if (e.target.matches('[data-tcb-remove]')) {
      if (slotsWrap.querySelectorAll('.tcb-slot').length > 1) { e.target.closest('.tcb-slot').remove(); renumber(); }
      return;
    }
    // Size quick-picks: single-select among siblings + fill the ranges.
    const sizeChip = e.target.closest('.tcb-chip[data-size]');
    if (sizeChip) {
      const s = sizeChip.closest('.tcb-slot');
      const wasOn = sizeChip.getAttribute('aria-pressed') === 'true';
      s.querySelectorAll('.tcb-chip[data-size]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
      if (wasOn) { clearRanges(s); return; } // tapping the active chip clears it
      sizeChip.setAttribute('aria-pressed', 'true');
      applySize(s, sizeChip.dataset.size);
      return;
    }
    // Style / material chips + color swatches: multi-toggle. (Size chips handled
    // above and excluded here so they never multi-toggle.)
    const chip = e.target.closest('.tcb-chip:not([data-size]), .tcb-sw');
    if (chip) { chip.setAttribute('aria-pressed', chip.getAttribute('aria-pressed') === 'true' ? 'false' : 'true'); return; }
  });

  // Editing a range by hand de-selects the size quick-pick (nicety).
  slotsWrap.addEventListener('input', (e) => {
    if (e.target.matches('.tcb-range input[type="number"]')) {
      e.target.closest('.tcb-slot').querySelectorAll('.tcb-chip[data-size][aria-pressed="true"]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
    }
  });

  loadFeed(); // warm the cache in the background (non-blocking)
  addSlot();  // start with one slot

  function collectSlots() {
    return [...slotsWrap.querySelectorAll('.tcb-slot')].map((s) => {
      const num = (f) => { const v = s.querySelector(`[data-f="${f}"]`)?.value; return v ? Number(v) : undefined; };
      const styles = [...s.querySelectorAll('.tcb-chip[data-style][aria-pressed="true"]')].map((b) => b.dataset.style);
      const colors = [...s.querySelectorAll('.tcb-sw[aria-pressed="true"]')].map((b) => b.dataset.color);
      const mustHaves = [...s.querySelectorAll('.tcb-chip[data-material][aria-pressed="true"]')].map((b) => b.dataset.material);
      const construction = s.querySelector('[data-f="construction"]')?.value || undefined;
      const tone = s.querySelector('[data-f="tone"]')?.value || undefined;
      const condition = s.querySelector('[data-f="condition"]')?.value || undefined;
      const ooakOnly = s.querySelector('[data-f="ooakOnly"]')?.checked || undefined;
      const shape = s.querySelector('[data-f="shape"]')?.value || undefined;
      return {
        styles: styles.length ? styles : undefined,
        colors: colors.length ? colors : undefined,
        mustHaves: mustHaves.length ? mustHaves : undefined,
        construction,
        tone,
        condition,
        ooakOnly,
        shape,
        widthMinFt: num('widthMinFt'), widthMinIn: num('widthMinIn'), widthMaxFt: num('widthMaxFt'), widthMaxIn: num('widthMaxIn'),
        lengthMinFt: num('lengthMinFt'), lengthMinIn: num('lengthMinIn'), lengthMaxFt: num('lengthMaxFt'), lengthMaxIn: num('lengthMaxIn'),
        budgetMax: num('budgetMax'),
      };
    });
  }
  const money = (c) => (c == null ? '' : '$' + (c / 100).toLocaleString('en-US', { maximumFractionDigits: 0 }));
  const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  // Append a width hint to the CDN image url (respecting any existing query).
  const imgAt = (url, w) => (url ? url + (url.includes('?') ? '&' : '?') + 'width=' + w : '');

  // ── Shortlist board + 12-at-a-time pagination state ──────────────────────────
  const PROJECT_KEY = 'tcb_board_' + location.pathname;
  const board = JSON.parse(localStorage.getItem(PROJECT_KEY) || '{}'); // { slotIndex: [picks] }
  const saveBoard = () => localStorage.setItem(PROJECT_KEY, JSON.stringify(board));
  const shownBySlot = {};   // slotIndex -> Set of shown product ids
  const baseBySlot = {};    // slotIndex -> full ranked array in original (best-match) order
  const rankedBySlot = {};  // slotIndex -> currently-sorted ranked array (what we page through)
  const cursorBySlot = {};  // slotIndex -> next index
  let sortMode = 'best';    // best | price-asc | price-desc
  function track(event, params) { if (window.gtag) window.gtag('event', event, Object.assign({ method: 'trade_brief' }, params || {})); }

  // Total saved across every rug room — drives the running "♡ N saved" badge.
  function totalSaved() { return Object.keys(board).reduce((n, k) => n + (board[k] ? board[k].length : 0), 0); }

  // Resolve a saved pick's retail price_cents: prefer the value stored on the pick;
  // fall back to the live results (sorted or base) for legacy picks saved before we
  // started persisting the price. Returns null when nothing is known.
  function pickPriceCents(slot, pick) {
    if (typeof pick.priceCents === 'number') return pick.priceCents;
    const pools = [rankedBySlot[slot], baseBySlot[slot]];
    for (const pool of pools) {
      if (!pool) continue;
      const m = pool.find((p) => p.rug.shopify_product_id === pick.id);
      if (m && typeof m.rug.price_cents === 'number') return m.rug.price_cents;
    }
    return null;
  }
  // Running project total = sum of the TRADE price of every saved pick across all rooms.
  function projectTotalCents() {
    let sum = 0;
    Object.keys(board).forEach((slot) => {
      (board[slot] || []).forEach((pick) => {
        const trade = M.tradePriceCents(pickPriceCents(slot, pick));
        if (typeof trade === 'number') sum += trade;
      });
    });
    return sum;
  }
  function updateSavedCount() {
    const el = root.querySelector('[data-saved-count]');
    if (el) {
      const n = totalSaved();
      el.dataset.count = n;
      el.querySelector('[data-saved-n]').textContent = n;
      el.querySelector('[data-saved-word]').textContent = n === 1 ? 'piece saved' : 'pieces saved';
    }
    // Running project budget total — sums trade prices across every room. Hidden at $0.
    const totalEl = root.querySelector('[data-project-total]');
    if (totalEl) {
      const cents = projectTotalCents();
      totalEl.hidden = cents <= 0;
      const amt = totalEl.querySelector('[data-project-total-amt]');
      if (amt) amt.textContent = money(cents);
    }
  }

  // Re-derive a slot's order from its untouched best-match base, per the active sort.
  function applySort(slotIndex) {
    const base = baseBySlot[slotIndex] || [];
    if (sortMode === 'price-asc' || sortMode === 'price-desc') {
      const dir = sortMode === 'price-asc' ? 1 : -1;
      // Nulls (price-on-request) always sort last regardless of direction.
      rankedBySlot[slotIndex] = base.slice().sort((a, b) => {
        const pa = a.rug.price_cents, pb = b.rug.price_cents;
        if (pa == null && pb == null) return 0;
        if (pa == null) return 1;
        if (pb == null) return -1;
        return (pa - pb) * dir;
      });
    } else {
      rankedBySlot[slotIndex] = base.slice(); // original matched order
    }
  }

  // ── The site product card, rebuilt in JS to mirror snippets/product-card.liquid ──
  // Same classes + DOM so global theme.css styles it identically to a collection
  // card. Single image (no hover-swap — the feed carries one image_url). Field map:
  //   .card-collection = first style (e.g. "Persian") else origin
  //   .card-name       = rug_name (fallback title)
  //   .card-descriptor = title
  //   .card-price      = struck retail + trade price (reads "trade")
  //   1-of-1 badge     = is_ooak; Masterpiece badge = masterpiece flag
  //   .card-fav        = save-to-board heart (data-save), saved state mirrored
  function cardHTML(p, saved) {
    const rug = p.rug;
    const id = rug.shopify_product_id;
    const name = rug.rug_name || rug.title || '';
    const hasName = !!(rug.rug_name && rug.title && rug.rug_name !== rug.title);
    const eyebrow = (rug.styles && rug.styles.length ? rug.styles[0] : rug.origin) || '';
    const url = rug.product_url || (rug.handle ? '/products/' + rug.handle : '#');
    const img = rug.image_url
      ? `<img class="base" src="${esc(imgAt(rug.image_url, 600))}" srcset="${esc(imgAt(rug.image_url, 600))} 600w, ${esc(imgAt(rug.image_url, 900))} 900w" sizes="(max-width:520px) 92vw, (max-width:880px) 45vw, 30vw" alt="${esc(name)}" loading="lazy" width="600" height="750">`
      : `<div class="tcb-noimg" aria-hidden="true"></div>`;
    const trade = M.tradePriceCents(rug.price_cents);
    const retail = rug.price_cents;
    const isSaved = saved.has(id);
    // Badges: 1-of-1 takes the top-left; a masterpiece badge sits top-right.
    let badges = '';
    if (rug.is_ooak) badges += `<span class="badge ooak"><span class="mk" aria-hidden="true">&#9670;</span>1 of 1</span>`;
    if (rug.masterpiece) badges += `<span class="badge master"><span class="mk" aria-hidden="true">&#9670;</span>Masterpiece</span>`;
    // Price: trade rate is the hero figure; retail struck through beside it.
    let price;
    if (trade) {
      price = `${retail ? `<s>${money(retail)}</s> ` : ''}${money(trade)} <span class="tcb-trade-tag">trade</span>`;
    } else {
      price = 'Price on request';
    }
    return `<article class="card reveal in tcb-card">
      <button class="card-fav${isSaved ? ' saved' : ''}" type="button"
              data-save="${esc(id)}" aria-pressed="${isSaved}"
              aria-label="${isSaved ? 'Remove' : 'Save'} ${esc(name)} to your shortlist">
        <span class="ch" aria-hidden="true">&#9825;</span>
      </button>
      <a class="card-core" href="${esc(url)}" aria-label="${esc(name)}${hasName ? ' — ' + esc(rug.title) : ''}">
        <div class="card-img">
          ${badges}
          ${img}
        </div>
        <div class="card-body">
          ${eyebrow ? `<div class="card-collection">${esc(eyebrow)}</div>` : ''}
          <h3 class="card-name">${esc(name)}</h3>
          ${hasName ? `<div class="card-descriptor">${esc(rug.title)}</div>` : ''}
          <div class="card-foot">
            <div class="card-price">${price}</div>
          </div>
        </div>
      </a>
    </article>`;
  }

  function renderBatch(slotIndex) {
    const ranked = rankedBySlot[slotIndex] || [];
    const shown = shownBySlot[slotIndex];
    const start = cursorBySlot[slotIndex] || 0;
    const batch = ranked.slice(start, start + 12);
    cursorBySlot[slotIndex] = start + batch.length;
    batch.forEach((p) => shown.add(p.rug.shopify_product_id));
    const saved = new Set((board[slotIndex] || []).map((x) => x.id));
    const cards = batch.map((p) => cardHTML(p, saved)).join('');
    const grid = document.querySelector(`[data-results="${slotIndex}"]`);
    grid.querySelector('.cards').insertAdjacentHTML('beforeend', cards);
    const oldMore = grid.querySelector('.tcb-more-actions'); if (oldMore) oldMore.remove();
    const more = cursorBySlot[slotIndex] < ranked.length
      ? `<button type="button" class="tcb-link tcb-fresh" data-fresh="${slotIndex}">Show me 12 fresh options <span aria-hidden="true">&#8594;</span></button>`
      : `<p class="tcb-allshown">That's our strongest matches for this rug — adjust the brief above for a fresh edit.</p>`;
    grid.insertAdjacentHTML('beforeend', `<div class="tcb-more-actions">${more}</div>`);
    renderBoard(slotIndex);
  }

  // Re-render a slot from scratch in the current sort order (used by the sort toggle).
  function rerenderSlot(slotIndex) {
    const grid = document.querySelector(`[data-results="${slotIndex}"]`);
    if (!grid) return;
    applySort(slotIndex);
    shownBySlot[slotIndex] = new Set();
    cursorBySlot[slotIndex] = 0;
    grid.querySelector('.cards').innerHTML = '';
    const old = grid.querySelector('.tcb-more-actions'); if (old) old.remove();
    if ((rankedBySlot[slotIndex] || []).length) renderBatch(slotIndex);
  }

  function renderBoard(slotIndex) {
    const el = document.querySelector(`[data-board="${slotIndex}"]`);
    if (!el) return;
    const picks = board[slotIndex] || [];
    el.innerHTML =
      `<div class="tcb-board-head">
        <h3>Your shortlist <span class="tcb-board-room">· Rug ${Number(slotIndex) + 1}</span> <span class="tcb-board-n">(${picks.length})</span></h3>
        ${picks.length ? `<button type="button" class="tcb-clear" data-clear="${slotIndex}">Clear shortlist</button>` : ''}
      </div>` +
      (picks.length
        ? `<div class="tcb-boardrow">` + picks.map((p) =>
            `<span class="tcb-bchip"><a href="${esc(p.url)}"><img src="${esc(imgAt(p.img, 160))}" alt="${esc(p.title)}" loading="lazy"></a><button type="button" class="tcb-bx" data-unsave="${slotIndex}:${esc(p.id)}" aria-label="Remove ${esc(p.title)} from shortlist">&times;</button></span>`
          ).join('') + `</div>`
        : `<p class="tcb-empty">Tap the heart on any piece above and it collects here — your edit for this rug.</p>`);
    updateSavedCount();
  }

  // Reflect a card's saved state across every rendered instance of that product.
  function syncCardHearts(id, isSaved) {
    document.querySelectorAll(`[data-save="${CSS.escape(id)}"]`).forEach((b) => {
      b.classList.toggle('saved', isSaved);
      b.setAttribute('aria-pressed', String(isSaved));
    });
  }

  /* One requirement line per rug, in the words the designer chose. `—` where they said nothing,
     so the reader can tell "no preference" from "not asked". */
  function slotSummary(sl, i) {
    const ft = (f, inch) => (f == null && inch == null ? null : `${f || 0}'${inch || 0}"`);
    const range = (a, b) => { const lo = ft(...a), hi = ft(...b); return lo && hi ? `${lo}–${hi}` : lo || hi || null; };
    const size = [
      range([sl.widthMinFt, sl.widthMinIn], [sl.widthMaxFt, sl.widthMaxIn]),
      range([sl.lengthMinFt, sl.lengthMinIn], [sl.lengthMaxFt, sl.lengthMaxIn]),
    ].filter(Boolean).join(' × ');
    const bits = [
      size && `size ${size}`,
      sl.shape && `${sl.shape}`,
      (sl.styles || []).length && `style ${sl.styles.join('/')}`,
      (sl.colors || []).length && `colour ${sl.colors.join('/')}`,
      (sl.mustHaves || []).length && `must have ${sl.mustHaves.join('/')}`,
      sl.construction && `${sl.construction}`,
      sl.tone && `${sl.tone} tone`,
      sl.condition && `${sl.condition}`,
      sl.ooakOnly && 'one-of-a-kind only',
      sl.budgetMax && `budget up to $${sl.budgetMax}`,
    ].filter(Boolean);
    return `Rug ${i + 1}: ${bits.length ? bits.join(', ') : '—'}`;
  }

  /*
   * ⛔ THIS USED TO POST TO SHOPIFY'S NATIVE CONTACT FORM, and the comment here said so plainly:
   * "operator inbox (OS pulls it in P2)". P2 was never built. Measured on production 2026-09-06:
   * ZERO trade briefs in the CRM, ever, against 30+ approved trade accounts — so the single richest
   * lead the site can produce (an approved designer, naming a project, choosing specific rugs) was
   * reaching nobody who could act on it.
   *
   * ⭑ IT NOW USES THE SAME APP PROXY DOOR AS EVERY OTHER FORM, declaring `type=brief`, so the lead
   * arrives typed `trade_brief` / `person_type: designer` rather than inferred from a customer tag.
   * The shortlist travels with it as prose, which is what turns "a designer enquired" into "a
   * designer wants these four rugs" for whoever opens the queue.
   *
   * ⛔ FIRE-AND-FORGET, exactly as before: the curated edit is what the designer asked for, and the
   * CRM hop must never be able to delay or break it.
   */
  async function captureBrief(projectName, slots, edits) {
    try {
      const d = root.dataset;
      const picks = edits.flatMap((e) => e.map((p) => p.rug))
        .filter((r, i, a) => r && a.findIndex((x) => x.shopify_product_id === r.shopify_product_id) === i);

      const erp = new FormData();
      erp.set('type', 'brief');
      erp.set('name', d.name || (d.email || '').split('@')[0] || 'Trade designer');
      erp.set('email', d.email || '');
      /* The enquiry door requires a reachable number; it comes from the trade account, so a
         signed-in designer is never asked to retype it. */
      if (d.phone) erp.set('phone', d.phone);
      if (d.company) erp.set('company', d.company);
      erp.set('project', projectName);
      erp.set('detail', [
        `Trade Concierge brief — ${slots.length} rug${slots.length === 1 ? '' : 's'}.`,
        ...slots.map(slotSummary),
      ].join('\n'));
      /* Capped server-side at 500 characters, so the most useful few lead. */
      if (picks.length) {
        erp.set('shortlist', picks.slice(0, 8)
          .map((r) => `${r.rug_name || r.title || r.shopify_product_id}${r.product_url ? ` (${r.product_url})` : ''}`)
          .join('; '));
      }
      await fetch((d.enquire || '/apps/trade/enquire') + '?type=brief',
        { method: 'POST', body: erp, keepalive: true });
    } catch (_) { /* the curated edit is what they asked for; never let the CRM hop break it */ }
  }

  // Results-area interaction: fresh-options pagination, save (♡ toggle), unsave (×),
  // clear-shortlist, and the sort control.
  results.addEventListener('click', (e) => {
    const fresh = e.target.closest('[data-fresh]');
    if (fresh) { renderBatch(fresh.dataset.fresh); track('trade_fresh_options', { slot: Number(fresh.dataset.fresh) }); return; }

    // ♡ TOGGLE — saved ⇒ remove from board (+ un-fill every instance); else add.
    const save = e.target.closest('[data-save]');
    if (save) {
      const slot = save.closest('[data-results]').dataset.results;
      const id = save.dataset.save;
      board[slot] = board[slot] || [];
      const already = board[slot].some((x) => x.id === id);
      if (already) {
        board[slot] = board[slot].filter((x) => x.id !== id);
        saveBoard(); renderBoard(slot); syncCardHearts(id, false);
        track('trade_rug_unsaved', { item_id: id, slot: Number(slot) });
      } else {
        const pick = (baseBySlot[slot] || []).find((p) => p.rug.shopify_product_id === id);
        if (pick) {
          // Store retail price_cents on the pick so the running project total can sum
          // trade prices without needing the live results in memory.
          board[slot].push({ id, title: pick.rug.rug_name || pick.rug.title, img: pick.rug.image_url, url: pick.rug.product_url || (pick.rug.handle ? '/products/' + pick.rug.handle : '#'), priceCents: pick.rug.price_cents });
          saveBoard(); renderBoard(slot); syncCardHearts(id, true);
          track('trade_rug_saved', { item_id: id, slot: Number(slot) });
        }
      }
      return;
    }

    // Board × — remove one pick (un-fills the matching card heart).
    const un = e.target.closest('[data-unsave]');
    if (un) {
      const idx = un.dataset.unsave.indexOf(':');
      const slot = un.dataset.unsave.slice(0, idx);
      const id = un.dataset.unsave.slice(idx + 1);
      board[slot] = (board[slot] || []).filter((x) => x.id !== id);
      saveBoard(); renderBoard(slot); syncCardHearts(id, false);
      return;
    }

    // Clear shortlist — confirm, wipe this room's picks, re-render empty, un-fill.
    const clear = e.target.closest('[data-clear]');
    if (clear) {
      const slot = clear.dataset.clear;
      if (!window.confirm('Clear every piece saved for Rug ' + (Number(slot) + 1) + '?')) return;
      const ids = (board[slot] || []).map((x) => x.id);
      board[slot] = [];
      saveBoard(); renderBoard(slot);
      ids.forEach((id) => syncCardHearts(id, false));
      return;
    }
  });

  // Sort toggle — re-derive every slot's order from its best-match base, re-render.
  results.addEventListener('change', (e) => {
    const sel = e.target.closest('[data-sort]');
    if (!sel) return;
    sortMode = sel.value;
    Object.keys(baseBySlot).forEach((i) => rerenderSlot(i));
    track('trade_sort', { sort: sortMode });
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = form.querySelector('[data-tcb-submit]') || form.querySelector('button[type="submit"]');
    if (btn) { btn.disabled = true; btn.dataset.label = btn.innerHTML; btn.textContent = 'Curating your edit…'; }
    try {
      const feed = await loadFeed();
      const slots = collectSlots();
      // Reset all in-memory result state for a clean re-run.
      Object.keys(rankedBySlot).forEach((k) => delete rankedBySlot[k]);
      Object.keys(baseBySlot).forEach((k) => delete baseBySlot[k]);
      results.hidden = false;

      // Results header: title + running saved-count + sort control (rendered once).
      let html = `<div class="tcb-results-head">
        <div class="tcb-results-headline">
          <p class="tcb-eyebrow">Curated at trade pricing</p>
          <h2 class="tcb-results-title editorial">Your edit</h2>
        </div>
        <div class="tcb-results-tools">
          <span class="tcb-projecttotal" data-project-total hidden>Project total: <b data-project-total-amt>$0</b></span>
          <span class="tcb-savedcount" data-saved-count data-count="0"><span class="tcb-heart" aria-hidden="true">&#9829;</span> <b data-saved-n>0</b> <span data-saved-word>pieces saved</span></span>
          <label class="tcb-sort"><span>Sort</span>
            <select data-sort aria-label="Sort the edit">
              <option value="best">Best match</option>
              <option value="price-asc">Price · low to high</option>
              <option value="price-desc">Price · high to low</option>
            </select>
          </label>
        </div>
      </div>`;

      // One editorial section per rug slot: subheading + grid + board panel beneath.
      slots.forEach((slot, i) => {
        html += `<section class="tcb-slot-result" data-results="${i}">
          <header class="tcb-slot-head">
            <p class="eyebrow">Rug ${i + 1}</p>
            <h3 class="editorial tcb-slot-title">Curated for this rug</h3>
          </header>
          <div class="cards" data-grid></div>
        </section>
        <section class="tcb-board" data-board="${i}" aria-label="Shortlist for rug ${i + 1}"></section>`;
      });
      results.innerHTML = html;
      sortMode = 'best';

      // Match + render each slot.
      slots.forEach((slot, i) => {
        let ranked = [];
        try { ranked = M.matchRugs(feed, Object.assign(M.formBriefToDesignBrief(slot), { count: 60 })); } catch (_) { ranked = []; }
        baseBySlot[i] = ranked;
        applySort(i);
        shownBySlot[i] = new Set();
        cursorBySlot[i] = 0;
        const grid = document.querySelector(`[data-results="${i}"]`);
        if (ranked.length) {
          renderBatch(i);
        } else {
          grid.querySelector('.cards').innerHTML =
            `<div class="tcb-noresult"><p class="tcb-noresult-h editorial">No match for this one — <em>yet</em></p><p>The gallery is one-of-a-kind, so a tight brief can come up empty. Loosen the size range or drop a color to widen the edit.</p></div>`;
          renderBoard(i);
        }
      });
      updateSavedCount();
      results.scrollIntoView({ behavior: 'smooth', block: 'start' });
      track('trade_brief_submitted', { slots: slots.length });
      try {
        const nameEl = form.querySelector('[data-f="projectName"]');
        captureBrief((nameEl && nameEl.value.trim()) || 'Untitled project', slots, slots.map((_, i) => baseBySlot[i] || []));
      } catch (_) {}
    } catch (err) {
      results.hidden = false;
      results.innerHTML = `<div class="tcb-noresult"><p class="tcb-noresult-h editorial">Something went wrong loading the gallery</p><p>Please refresh and try again — your shortlist is saved.</p></div>`;
    } finally {
      if (btn) { btn.disabled = false; btn.innerHTML = btn.dataset.label || 'Get my curated edit'; }
    }
  });

  // ── Start fresh — reset to one blank slot, clear results, KEEP the board. ──
  const resetBtn = root.querySelector('[data-tcb-reset]');
  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      slotsWrap.querySelectorAll('.tcb-slot').forEach((s) => s.remove());
      addSlot();
      const nameEl = form.querySelector('[data-f="projectName"]'); if (nameEl) nameEl.value = '';
      results.innerHTML = ''; results.hidden = true;
      Object.keys(rankedBySlot).forEach((k) => delete rankedBySlot[k]);
      Object.keys(baseBySlot).forEach((k) => delete baseBySlot[k]);
      Object.keys(shownBySlot).forEach((k) => delete shownBySlot[k]);
      Object.keys(cursorBySlot).forEach((k) => delete cursorBySlot[k]);
      sortMode = 'best';
      form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }
})();
